(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.EpicArtworkGuideCore = api;
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';

  var GUIDE_VERSION = 'safe-area-v6';
  var GUIDE_SHAPE_PREFIX = 'epic-artwork-guide-';
  var SAFE_AREA_CM = 2.5;

  /*
   * Keep this list aligned with the Upload-Lift product mappings. Dimensions are
   * physical finished sizes, not upload pixels and not an extra bleed canvas.
   */
  var PRODUCT_PROFILES = Object.freeze({
    '4686619541586': Object.freeze({ widthCm: 70, heightCm: 30, label: '70 × 30 cm' }),
    '4865967521874': Object.freeze({ widthCm: 70, heightCm: 30, label: '70 × 30 cm RGB' }),
    '4686743470162': Object.freeze({ widthCm: 90, heightCm: 40, label: '90 × 40 cm' }),
    '4867140517970': Object.freeze({ widthCm: 90, heightCm: 40, label: '90 × 40 cm RGB' }),
    '7509021687890': Object.freeze({ widthCm: 90, heightCm: 40, label: '90 × 40 cm playmat' }),
    '4686739931218': Object.freeze({ widthCm: 80, heightCm: 30, label: '80 × 30 cm' }),
    '4865975025746': Object.freeze({ widthCm: 80, heightCm: 30, label: '80 × 30 cm RGB' }),
    '7481189531730': Object.freeze({ widthCm: 80, heightCm: 40, label: '80 × 40 cm RGB' }),
    '6925805879378': Object.freeze({ widthCm: 80, heightCm: 40, label: '80 × 40 cm' }),
    '6920084619346': Object.freeze({ widthCm: 35, heightCm: 25, label: '35 × 25 cm' }),
    '7479771594834': Object.freeze({ widthCm: 90, heightCm: 30, label: '90 × 30 cm' }),
    '4867138584658': Object.freeze({ widthCm: 90, heightCm: 30, label: '90 × 30 cm RGB' }),
    '7479807672402': Object.freeze({ widthCm: 100, heightCm: 50, label: '100 × 50 cm' }),
    '7481190580306': Object.freeze({ widthCm: 100, heightCm: 50, label: '100 × 50 cm RGB' }),
    '7500576948306': Object.freeze({ widthCm: 60, heightCm: 35, label: '60 × 35 cm playmat' }),
    '7504705257554': Object.freeze({ widthCm: 120, heightCm: 50, label: '120 × 50 cm' }),
    '7509017362514': Object.freeze({ widthCm: 70, heightCm: 35, label: '70 × 35 cm playmat' }),
    '7509165572178': Object.freeze({ widthCm: 49, heightCm: 42, label: '49 × 42 cm Pro' })
  });

  function finitePositive(value) {
    return Number.isFinite(Number(value)) && Number(value) > 0;
  }

  function clamp(value, minimum, maximum) {
    return Math.max(minimum, Math.min(maximum, value));
  }

  function getProfile(productId) {
    var profile = PRODUCT_PROFILES[String(productId || '')];
    if (!profile) return null;
    return {
      productId: String(productId),
      widthCm: profile.widthCm,
      heightCm: profile.heightCm,
      label: profile.label,
      safeAreaCm: SAFE_AREA_CM
    };
  }

  function parseAspectRatio(value) {
    if (typeof value === 'string' && value.indexOf(':') !== -1) {
      var parts = value.split(':');
      if (parts.length !== 2 || !finitePositive(parts[0]) || !finitePositive(parts[1])) return null;
      return Number(parts[0]) / Number(parts[1]);
    }
    return finitePositive(value) ? Number(value) : null;
  }

  function cropRatioMatchesProfile(profile, configuredRatio) {
    if (!profile || !finitePositive(profile.widthCm) || !finitePositive(profile.heightCm)) return false;
    var parsed = parseAspectRatio(configuredRatio);
    if (!parsed) return false;
    return Math.abs(parsed - Number(profile.widthCm) / Number(profile.heightCm)) <= 0.002;
  }

  function computeGuideRect(selectionRect, profile) {
    if (!selectionRect || !profile) return null;
    if (
      !finitePositive(selectionRect.width) ||
      !finitePositive(selectionRect.height) ||
      !finitePositive(profile.widthCm) ||
      !finitePositive(profile.heightCm) ||
      !finitePositive(profile.safeAreaCm)
    ) {
      return null;
    }

    var safeAreaCm = Number(profile.safeAreaCm);
    if (safeAreaCm * 2 >= Number(profile.widthCm) || safeAreaCm * 2 >= Number(profile.heightCm)) {
      return null;
    }

    var x = Number(selectionRect.x) || 0;
    var y = Number(selectionRect.y) || 0;
    var width = Number(selectionRect.width);
    var height = Number(selectionRect.height);
    var insetX = width * safeAreaCm / Number(profile.widthCm);
    var insetY = height * safeAreaCm / Number(profile.heightCm);

    return {
      selection: { x: x, y: y, width: width, height: height },
      safe: {
        x: x + insetX,
        y: y + insetY,
        width: width - insetX * 2,
        height: height - insetY * 2
      },
      insetX: insetX,
      insetY: insetY
    };
  }

  function rectangle(x, y, width, height, opacity) {
    return {
      x: x,
      y: y,
      width: Math.max(0, width),
      height: Math.max(0, height),
      backgroundColor: [0.03, 0.03, 0.03, 0.52],
      opacity: opacity,
      alwaysOnTop: true
    };
  }

  function safeOutline(rect, opacity, accent) {
    return {
      points: [
        { x: rect.x, y: rect.y },
        { x: rect.x + rect.width, y: rect.y },
        { x: rect.x + rect.width, y: rect.y + rect.height },
        { x: rect.x, y: rect.y + rect.height }
      ],
      pathClose: true,
      bitmap: true,
      strokeJoin: 'round',
      strokeColor: accent ? [1, 0.38, 0, 1] : [0, 0, 0, 0.92],
      strokeWidth: accent ? 4 : 8,
      opacity: opacity,
      alwaysOnTop: true
    };
  }

  function safeLabelShapes(rect, opacity, selectionRect) {
    var shortestSide = Math.min(selectionRect.width, selectionRect.height);
    var fontSize = clamp(shortestSide / 27, 11, 17);
    var outerPadding = clamp(rect.width * 0.025, 7, 14);
    var innerPadding = clamp(fontSize * 0.55, 6, 10);
    var labelWidth = Math.min(
      rect.width - outerPadding * 2,
      Math.ceil(fontSize * 23.5 + innerPadding * 2)
    );
    var labelHeight = Math.ceil(fontSize * 1.55);
    var x = rect.x + outerPadding;
    var y = rect.y + outerPadding;

    return [
      {
        x: x,
        y: y,
        width: labelWidth,
        height: labelHeight,
        backgroundColor: [0, 0, 0, 0.68],
        opacity: opacity,
        alwaysOnTop: true
      },
      {
        x: x + innerPadding,
        y: y + Math.max(1, Math.round((labelHeight - fontSize * 1.2) / 2)),
        text: 'KEEP TEXT & LOGOS INSIDE ORANGE LINE',
        fontFamily: 'Arial',
        fontSize: fontSize,
        fontWeight: 'bold',
        lineHeight: 1.2,
        color: [1, 1, 1, 1],
        opacity: opacity,
        alwaysOnTop: true
      }
    ];
  }

  function guideShape(name, shape) {
    return Object.assign({ id: GUIDE_SHAPE_PREFIX + name }, shape);
  }

  function isGuideShape(shape) {
    return Boolean(
      shape &&
      typeof shape.id === 'string' &&
      shape.id.indexOf(GUIDE_SHAPE_PREFIX) === 0
    );
  }

  function addSafeAreaGuide(shapes, state, profile) {
    var base = shapes && typeof shapes === 'object' ? shapes : {};
    var currentInterfaceShapes = Array.isArray(base.interfaceShapes) ? base.interfaceShapes : [];
    var existingInterfaceShapes = currentInterfaceShapes.filter(function (shape) {
      return !isGuideShape(shape);
    });
    var baseWithoutGuide = existingInterfaceShapes.length === currentInterfaceShapes.length
      ? base
      : Object.assign({}, base, { interfaceShapes: existingInterfaceShapes });
    var visibility = state && state.utilVisibility && Number(state.utilVisibility.crop);
    if (Number.isFinite(visibility) && visibility <= 0) return baseWithoutGuide;

    var guide = computeGuideRect(state && state.selectionRect, profile);
    if (!guide) return baseWithoutGuide;

    var opacity = Number.isFinite(visibility) ? clamp(visibility, 0, 1) : 1;
    var selection = guide.selection;
    var safe = guide.safe;
    var overlays = [
      guideShape(
        'risk-top',
        rectangle(selection.x, selection.y, selection.width, guide.insetY, opacity)
      ),
      guideShape(
        'risk-bottom',
        rectangle(
          selection.x,
          selection.y + selection.height - guide.insetY,
          selection.width,
          guide.insetY,
          opacity
        )
      ),
      guideShape(
        'risk-left',
        rectangle(
          selection.x,
          selection.y + guide.insetY,
          guide.insetX,
          selection.height - guide.insetY * 2,
          opacity
        )
      ),
      guideShape(
        'risk-right',
        rectangle(
          selection.x + selection.width - guide.insetX,
          selection.y + guide.insetY,
          guide.insetX,
          selection.height - guide.insetY * 2,
          opacity
        )
      ),
      guideShape('outline-underlay', safeOutline(safe, opacity, false)),
      guideShape('outline-orange', safeOutline(safe, opacity, true))
    ];

    if (safe.width >= 260 && safe.height >= 55) {
      var labelShapes = safeLabelShapes(safe, opacity, selection);
      overlays.push(guideShape('label-background', labelShapes[0]));
      overlays.push(guideShape('label-text', labelShapes[1]));
    }

    return Object.assign({}, baseWithoutGuide, {
      interfaceShapes: overlays.concat(existingInterfaceShapes)
    });
  }

  function createWillRenderCanvas(profile, previousHook) {
    var hook = function (shapes, state) {
      var base = shapes;
      if (typeof previousHook === 'function') {
        try {
          base = previousHook(shapes, state) || shapes;
        } catch (error) {
          if (typeof console !== 'undefined' && typeof console.error === 'function') {
            console.error('[Epic artwork guide] Existing canvas hook failed.', error);
          }
        }
      }
      return addSafeAreaGuide(base, state, profile);
    };
    hook.epicArtworkGuideVersion = GUIDE_VERSION;
    return hook;
  }

  return Object.freeze({
    GUIDE_VERSION: GUIDE_VERSION,
    SAFE_AREA_CM: SAFE_AREA_CM,
    PRODUCT_PROFILES: PRODUCT_PROFILES,
    getProfile: getProfile,
    parseAspectRatio: parseAspectRatio,
    cropRatioMatchesProfile: cropRatioMatchesProfile,
    computeGuideRect: computeGuideRect,
    addSafeAreaGuide: addSafeAreaGuide,
    createWillRenderCanvas: createWillRenderCanvas
  });
});
